import { useContext, useState } from 'react'
import { RegisterContext } from '../../context/RegisterContext'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import { dateParser, validateFormInfo } from '../../helpers/utils'
import CustomDatePicker from '../../components/DatePicker'
import { DEFAULT_EDUCATIONAL_INFO, TEducationalInfo } from '../../hooks/useFormRegister'
import CustomCheckbox from '../../components/Checkbox'
import { Country, RegisterFormInfoTypes } from '../../helpers/enums'
import CustomDropdown from '../../components/Dropdown'
import dayjs from 'dayjs'
import { AppConfig } from '../../models/shared'
import CustomIconButton from '../../components/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import SearchAbilitiesModal from '../../components/Modal/SearchAbilitiesModal'
import CustomModal from '../../containers/Modal'
import { Chip } from '@mui/material'

interface Props {
    handleClose: () => void
    id?: string
    dateRef: string
    config?: AppConfig
}
const EducationForm = ({ handleClose, id, dateRef, config }: Props) => {
    const index = parseInt(id as string) - 1
    const { educationalInfo, saveInfo } = useContext(RegisterContext)
    const [isAbilitiesModalOpen, setIsAbilitiesModalOpen] = useState<boolean>(false)
    const [present, setPresent] = useState<boolean>(
        id ? educationalInfo[index].endingDate === '' : false,
    )
    const [info, setInfo] = useState<TEducationalInfo>(
        id
            ? educationalInfo[index]
            : config
              ? {
                    id: '',
                    degree: 'Universitario',
                    school: config.university,
                    carrer: '',
                    startDate: '',
                    endingDate: '',
                    country: 'Argentina',
                    abilities: [],
                }
              : DEFAULT_EDUCATIONAL_INFO,
    )

    const handleOnChange = (name: string, value: string) => {
        setInfo((prev) => ({ ...prev, [name]: value }))
    }

    const validateForm = () => {
        return validateFormInfo(info) && (info.endingDate !== '' || present)
    }

    const handleSubmit = (info: TEducationalInfo, id: string | undefined) => {
        if (!validateFormInfo(info)) return

        saveInfo(RegisterFormInfoTypes.Educational, info, id)
        handleClose()
    }

    const handlePresentCheckbox = (checked: boolean) => {
        setPresent(checked)
        if (checked) handleOnChange('endingDate', '')
    }

    return (
        <div className="flex flex-col w-[70vw] md:w-[45vw] overflow-auto max-h-[60vh] md:max-h-[70vh] px-0.5 md:px-2 gap-3 md:gap-4 text-primary">
            <span className="text-base md:text-xl">Educación</span>
            <div className="grid gap-2 md:gap-4 mt-2 md:mt-4">
                <CustomDropdown
                    label="Nivel"
                    name="degree"
                    value={info.degree ?? ''}
                    onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                    data={[
                        'Certificación',
                        'Curso',
                        'Examen',
                        'Posgrado',
                        'Secundario',
                        'Terciario',
                        'Universitario',
                        'Otro',
                    ]}
                    disabled={Boolean(config)}
                />
                <CustomInput
                    type="text"
                    label="Institución"
                    name="school"
                    value={info.school}
                    onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    disabled={Boolean(config)}
                />
                {config ? (
                    <CustomDropdown
                        label="Carrera"
                        name="carrer"
                        value={info.carrer ?? ''}
                        onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                        data={config.careers}
                    />
                ) : (
                    <CustomInput
                        type="text"
                        label="Carrera"
                        name="carrer"
                        value={info.carrer}
                        onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    />
                )}
                <div className="grid gird-cols-1 md:grid-cols-[auto_120px] gap-2 md:gap-4 w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 w-full">
                        <CustomDatePicker
                            label="Fecha de Inicio"
                            name="startDate"
                            value={info.startDate}
                            onChange={handleOnChange}
                            maxValue={dayjs(Date.now())}
                            onlyMonthAndYear
                        />
                        <CustomDatePicker
                            label="Fecha de Fin"
                            name="endingDate"
                            value={info.endingDate}
                            onChange={handleOnChange}
                            disabled={present || info.startDate === ''}
                            minValue={dayjs(dateParser(info.startDate as string))}
                            maxValue={dayjs(Date.now())}
                            onlyMonthAndYear
                        />
                    </div>
                    {!config && (
                        <div className="flex justify-center items-center text-[#777777]">
                            Actualidad
                            <CustomCheckbox
                                checked={present}
                                className="text-[#666666]"
                                onChange={(e) => handlePresentCheckbox(e.target.checked)}
                            />
                        </div>
                    )}
                </div>
                <CustomDropdown
                    label="País"
                    name="country"
                    data={Object.keys(Country)}
                    value={info.country ?? ''}
                    onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                    disabled={Boolean(config)}
                />
                <div className="flex gap-2 items-center text-[#777777] ml-1 -my-1">
                    <span className="text-sm md:text-base">Aptitudes</span>
                    <CustomIconButton
                        className="h-fit text-gray"
                        icon={<EditIcon />}
                        onClick={() => setIsAbilitiesModalOpen(true)}
                    />
                </div>
                {info.abilities && info.abilities.length > 0 ? (
                    <div className="flex gap-1 items-center flex-wrap ml-1">
                        {info.abilities.map((ability) => (
                            <div key={`selected${ability}`}>
                                <Chip label={ability} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="ml-1 text-[#777777] text-xs md:text-base">
                        Seleccioná las aptitudes o habilidades que relaciones con esta experiencia
                        (opcional).
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-2 md:gap-4">
                <CustomButton
                    text="Volver"
                    className="w-full mt-2 md:mt-6 h-[36px]"
                    onClick={handleClose}
                    secondary
                />
                <CustomButton
                    text={id ? 'Actualizar' : 'Agregar'}
                    className="w-full mt-2 md:mt-6 h-[36px]"
                    onClick={() => handleSubmit(info, id)}
                    disabled={!validateForm()}
                />
            </div>
            <CustomModal
                isOpen={isAbilitiesModalOpen}
                handleClose={() => setIsAbilitiesModalOpen(false)}
            >
                <SearchAbilitiesModal
                    text="Seleccionar aptitudes"
                    handleClose={() => setIsAbilitiesModalOpen(false)}
                    selectedAbilities={info.abilities ?? []}
                    setSelectedAbility={(ability: string) =>
                        setInfo((prev) => ({
                            ...prev,
                            abilities: prev.abilities?.includes(ability)
                                ? prev.abilities.filter((x) => x !== ability)
                                : [...(prev.abilities ?? []), ability],
                        }))
                    }
                />
            </CustomModal>
        </div>
    )
}

export default EducationForm
